import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ANDROID_DIRECT_APK_DOWNLOAD_LINK,
  ANDROID_PLAY_STORE_WEB_DOWNLOAD_LINK,
  shouldShowAndroidDownloadChoice,
} from '../../utils/androidDownload';
import { getAmplifyVisitorRegion } from '../../utils/amplifyGeo';

type AndroidDownloadMode = 'checking' | 'choice';

export default function AndroidDownloadRedirect() {
  const { t } = useTranslation();
  const [mode, setMode] = useState<AndroidDownloadMode>('checking');

  const openGooglePlay = useCallback(() => {
    window.location.href = ANDROID_PLAY_STORE_WEB_DOWNLOAD_LINK;
  }, []);

  const downloadApkDirectly = useCallback(() => {
    window.location.href = ANDROID_DIRECT_APK_DOWNLOAD_LINK;
  }, []);

  useEffect(() => {
    let active = true;

    getAmplifyVisitorRegion()
      .then((region) => {
        if (!active) return;

        const shouldShowChoice = shouldShowAndroidDownloadChoice(region.countryCode);

        if (typeof console !== 'undefined') {
          console.info('[JL金利寶] Android MT5 download decision:', {
            countryCode: region.countryCode,
            shouldShowChoice,
            apkLink: ANDROID_DIRECT_APK_DOWNLOAD_LINK,
            globalLink: ANDROID_PLAY_STORE_WEB_DOWNLOAD_LINK,
            source: region.source,
          });
        }

        if (shouldShowChoice) {
          setMode('choice');
          return;
        }

        // Other regions do not need the special choice page.
        window.location.replace(ANDROID_PLAY_STORE_WEB_DOWNLOAD_LINK);
      })
      .catch((error) => {
        if (typeof console !== 'undefined') {
          console.warn('[JL金利寶] Android MT5 region check failed:', error);
        }

        if (!active) return;

        // Fail-open: unknown regions go to the original global / Google Play link.
        window.location.replace(ANDROID_PLAY_STORE_WEB_DOWNLOAD_LINK);
      });

    return () => {
      active = false;
    };
  }, []);

  if (mode === 'checking') {
    return (
      <main
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        }}
      >
        <section
          style={{
            width: '100%',
            maxWidth: 520,
            padding: 30,
            borderRadius: 20,
            background: '#ffffff',
            boxShadow: '0 16px 48px rgba(15, 23, 42, 0.12)',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              margin: '0 auto 18px',
              borderRadius: 18,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#111827',
              color: '#ffffff',
              fontSize: 34,
            }}
            aria-hidden="true"
          >
            <i className="ri-android-fill" />
          </div>

          <h1
            style={{
              margin: '0 0 12px',
              fontSize: 28,
              lineHeight: 1.25,
              color: '#111827',
              fontWeight: 700,
            }}
          >
            {t('androidDownload.title')}
          </h1>

          <p
            style={{
              margin: 0,
              fontSize: 15,
              lineHeight: 1.7,
              color: '#4b5563',
            }}
          >
            {t("androidDownload.checking", { defaultValue: "正在識別您的地區，請稍候。" })}
          </p>
        </section>
      </main>
    );
  }

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <section
        style={{
          width: '100%',
          maxWidth: 600,
          padding: 30,
          borderRadius: 20,
          background: '#ffffff',
          boxShadow: '0 16px 48px rgba(15, 23, 42, 0.12)',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            margin: '0 auto 18px',
            borderRadius: 18,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#111827',
            color: '#ffffff',
            fontSize: 34,
          }}
          aria-hidden="true"
        >
          <i className="ri-android-fill" />
        </div>

        <h1
          style={{
            margin: '0 0 12px',
            fontSize: 28,
            lineHeight: 1.25,
            color: '#111827',
            fontWeight: 700,
          }}
        >
          {t("androidDownload.choiceTitle", { defaultValue: "Android MT5 安裝下載" })}
        </h1>

        <p
          style={{
            margin: '0 0 18px',
            fontSize: 15,
            lineHeight: 1.7,
            color: '#4b5563',
          }}
        >
          {t("androidDownload.choiceDescription", { defaultValue: "系統已識別您位於中國內地或香港地區。建議優先使用金利寶 APK 安裝版；如您的裝置可正常使用 Google Play，亦可選擇官方下載連接。" })}
        </p>

        <p
          style={{
            margin: '0 0 24px',
            padding: '10px 14px',
            borderRadius: 12,
            background: '#fef3c7',
            color: '#92400e',
            fontSize: 14,
            lineHeight: 1.6,
            fontWeight: 600,
          }}
        >
          {t("androidDownload.choiceHint", { defaultValue: "中國內地 / 香港 Android 用戶如無法使用 Google Play，請選擇「金利寶 APK 安裝版」。" })}
        </p>

        <div
          style={{
            display: 'grid',
            gap: 14,
          }}
        >
          <button
            type="button"
            onClick={downloadApkDirectly}
            style={{
              width: '100%',
              minHeight: 54,
              border: 'none',
              borderRadius: 999,
              background: '#111827',
              color: '#ffffff',
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {t("androidDownload.apkOption", { defaultValue: "1、金利寶 APK 安裝版" })}
          </button>

          <button
            type="button"
            onClick={openGooglePlay}
            style={{
              width: '100%',
              minHeight: 54,
              border: '1px solid #d1d5db',
              borderRadius: 999,
              background: '#ffffff',
              color: '#111827',
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {t("androidDownload.officialOption", { defaultValue: "2、直達 Google Play / 官方下載" })}
          </button>
        </div>

        <p
          style={{
            margin: '18px 0 0',
            fontSize: 13,
            lineHeight: 1.6,
            color: '#6b7280',
          }}
        >
          {t("androidDownload.otherRegions", { defaultValue: "其他地區客戶會自動前往官方 Google Play / MetaQuotes 下載連接，不會顯示此選擇頁。" })}
        </p>
      </section>
    </main>
  );
}
